/**
 * DesktopHeader — primary navigation surface for tablet/laptop/desktop.
 *
 * Why this exists:
 *   Mobile users get the fixed BottomNav at the bottom of the viewport
 *   (Home, Nutrition, Family, Services, Support, Profile). On desktop the
 *   BottomNav is hidden (`md:hidden`) which previously left desktop users
 *   without any way to navigate — a hard beta blocker.
 *
 *   This component renders a sticky top header on `md:` and above that
 *   mirrors the BottomNav links exactly, plus the global utilities
 *   (search trigger, notifications, avatar dropdown with Logout) so
 *   nothing reachable from mobile becomes inaccessible on desktop.
 *
 * Design choices:
 *   - Visible only on md (≥768px); BottomNav still owns mobile.
 *   - Hidden on auth/marketing/vendor routes (same allowlist as BottomNav).
 *   - Sticky `top-0 z-40` — sits above page sticky headers (which we bump
 *     to `md:top-16` so they tuck cleanly under this bar).
 *   - 6 nav links match BottomNav 1:1; same routes, same localized labels.
 *   - Avatar dropdown surfaces every "deeper" entry the mobile dashboard
 *     header exposes: Health Profile, Notifications, Family Accounts,
 *     Reminders, Logout — so desktop parity is complete.
 */

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Apple,
  Users,
  HeartHandshake,
  HelpCircle,
  User,
  Bell,
  Settings,
  LogOut,
  HeartPulse,
  UsersRound,
} from 'lucide-react';
import HealthLoopLogo from '@/components/HealthLoopLogo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { GlobalSearchTrigger } from '@/components/GlobalSearch';
import { useGlobalSearchContext } from '@/contexts/GlobalSearchContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

const AUTH_OR_MARKETING_PAGES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/landing',
  '/onboarding',
  '/how-it-works',
  '/vendor-login',
  '/vendor-register',
];

const DesktopHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const { openSearch } = useGlobalSearchContext();
  const [, setMenuOpen] = useState(false);

  // Skip on landing + auth/marketing pages
  const shouldHide =
    location.pathname === '/' ||
    AUTH_OR_MARKETING_PAGES.some((p) => location.pathname.startsWith(p));

  if (shouldHide) return null;

  const navItems = [
    { path: '/dashboard',     labelKey: 'nav.dashboard', icon: Home },
    { path: '/nutrition',     labelKey: 'nav.nutrition', icon: Apple },
    { path: '/family-health', labelKey: 'nav.family',    icon: Users },
    { path: '/care-services', labelKey: 'nav.care',      icon: HeartHandshake },
    { path: '/support-help',  labelKey: 'nav.support',   icon: HelpCircle },
    { path: '/profile',       labelKey: 'nav.profile',   icon: User },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const getInitials = () => {
    const raw = (user?.name || '').trim();
    if (!raw) return user?.email?.[0]?.toUpperCase() || '?';
    const parts = raw.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || '?';
  };

  const handleLogout = async () => {
    try { await logout(); } catch (_) { /* still navigate even if API fails */ }
    navigate('/login');
  };

  return (
    <>
      <header
        className="hidden md:block sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-gray-100 shadow-sm"
        data-testid="desktop-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo — links to dashboard */}
            <Link
              to="/dashboard"
              className="flex items-center gap-2 shrink-0 rounded-xl px-1 py-1 transition-colors hover:bg-emerald-50/60"
              data-testid="desktop-header-logo"
              aria-label="HealthLoop home"
            >
              <HealthLoopLogo size="sm" showText={true} />
            </Link>

            {/* Primary nav — horizontal links */}
            <nav
              className="flex items-center gap-1 lg:gap-2"
              aria-label="Primary"
              data-testid="desktop-header-nav"
            >
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`desktop-nav-${item.path.replace('/', '')}`}
                    aria-current={active ? 'page' : undefined}
                    title={t(item.labelKey)}
                    className={`group inline-flex items-center gap-2 px-2 lg:px-4 py-2 rounded-full text-sm font-medium transition-all duration-150 ${
                      active
                        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 shadow-sm'
                        : 'text-gray-700 hover:text-emerald-700 hover:bg-emerald-50/70'
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 transition-transform ${
                        active ? 'scale-110' : 'group-hover:scale-105'
                      }`}
                      aria-hidden="true"
                    />
                    <span className="hidden lg:inline">{t(item.labelKey)}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Right utilities — search, notifications, avatar menu */}
            <div className="flex items-center gap-1 lg:gap-2 shrink-0">
              <GlobalSearchTrigger variant="icon" onClick={openSearch} />

              <Link
                to="/notifications"
                className="relative p-2 rounded-full text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                aria-label={t('dashboardDeep.notificationsAria') || 'Notifications'}
                data-testid="desktop-header-notifications"
              >
                <Bell className="h-5 w-5" />
              </Link>

              <DropdownMenu onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="relative ml-1 outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 rounded-full transition-transform duration-150 active:scale-95 hover:scale-105"
                    aria-label={t('dashboardDeep.profileMenuAria') || 'Open profile menu'}
                    data-testid="desktop-header-profile-btn"
                  >
                    <span
                      className="flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 via-teal-500 to-emerald-600 text-white text-sm font-bold shadow-md ring-2 ring-white"
                      data-testid="desktop-header-avatar-initials"
                    >
                      {getInitials()}
                    </span>
                    {(() => {
                      const verified = !!user?.email_verified;
                      const dotColor = verified ? 'bg-emerald-500' : 'bg-amber-400';
                      return (
                        <span
                          className={`absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full ring-2 ring-white ${dotColor}`}
                          aria-hidden="true"
                        />
                      );
                    })()}
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  sideOffset={10}
                  className="w-64 rounded-xl shadow-lg border-gray-100"
                  data-testid="desktop-header-profile-menu"
                >
                  <DropdownMenuLabel className="px-3 py-3">
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span
                        className="text-sm font-semibold text-gray-900 truncate"
                        data-testid="desktop-profile-menu-name"
                      >
                        {user?.name || t('profileDeep.healthLoopUser') || 'HealthLoop user'}
                      </span>
                      <span
                        className="text-xs font-normal text-gray-500 truncate"
                        data-testid="desktop-profile-menu-email"
                      >
                        {user?.email || ''}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate('/profile')}
                    className="gap-2 cursor-pointer"
                    data-testid="desktop-profile-menu-health-profile"
                  >
                    <HeartPulse className="h-4 w-4 text-emerald-600" />
                    <span>{t('dashboardDeep.healthProfile') || 'Health profile'}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/notifications')}
                    className="gap-2 cursor-pointer"
                    data-testid="desktop-profile-menu-notifications"
                  >
                    <Bell className="h-4 w-4 text-emerald-600" />
                    <span>{t('dashboardDeep.notifications') || 'Notifications'}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/family-health')}
                    className="gap-2 cursor-pointer"
                    data-testid="desktop-profile-menu-family"
                  >
                    <UsersRound className="h-4 w-4 text-emerald-600" />
                    <span>{t('dashboardDeep.familyAccounts') || 'Family accounts'}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/settings/reminders')}
                    className="gap-2 cursor-pointer"
                    data-testid="desktop-profile-menu-settings"
                  >
                    <Settings className="h-4 w-4 text-emerald-600" />
                    <span>{t('dashboardDeep.settings') || 'Settings'}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="gap-2 cursor-pointer text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                    data-testid="desktop-profile-menu-logout"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>{t('dashboardDeep.logout') || t('nav.logout') || 'Logout'}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default DesktopHeader;
